import React, { useMemo } from 'react';

import { PrimaryButton } from 'components/uiLibrary/LinkButton';
import Typography from 'components/uiLibrary/Typography';

import { SECTIONS } from 'components/Globals/Analytics/constants';
import EntityCard from 'components/Globals/EntityCard';
// eslint-disable-next-line import/no-cycle
import { usePaywallContext } from 'components/Paywall';

import { TP } from 'constants/index';
import { SUBSCRIPTION_PAYMENT_STATUSES, PRODUCT_ID_PRECEDENCE_ORDER } from 'constants/subscriptions';

import { createDate } from 'utils/date';
import { useProfileAccesses, useUserData } from 'utils/hooks/useAuthenticatedUser';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './PaymentDueBanner.module.scss';

const PaymentDueBanner = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const user = useUserData();
  const { setShowSubscriptionPlans } = usePaywallContext();
  const { data: profileAccesses } = useProfileAccesses();
  const primaryPaymentDueEntity = useMemo(
    () =>
      profileAccesses?.data
        ?.reduce((acc, accessInfo) => {
          let entityType;
          let entity;

          if (accessInfo.agency?.id) {
            entityType = ENTITY_TYPES.AGENCY;
            entity = accessInfo.agency;
          } else if (accessInfo.organization?.id) {
            entityType = ENTITY_TYPES.ORGANIZATION;
            entity = accessInfo.organization;
          } else if (accessInfo.profile?.id) {
            entityType = ENTITY_TYPES.PROFILE;
            entity = accessInfo.profile;
          } else if (accessInfo.user?.id) {
            entityType = ENTITY_TYPES.PROFILE;
            entity = accessInfo.user;
          }

          if (entityType && entity) {
            const invoiceDueDate = entity.subscription?.invoice?.due_date;
            const invoiceStatus = entity.subscription?.invoice?.status;
            const isDue = invoiceDueDate && createDate(invoiceDueDate * 1000).isSameOrAfter(createDate(), 'minute');
            const isOverdue = invoiceDueDate && createDate(invoiceDueDate * 1000).isBefore(createDate(), 'minute');
            const isUnpaid = invoiceStatus !== SUBSCRIPTION_PAYMENT_STATUSES.PAID;
            const isPaymentDue = !!entity.subscription?.id && isUnpaid && (isDue || isOverdue);
            const isRelevantSubscription =
              ENTITY_TYPES.PROFILE === entityType ? entity?.subscription?.user?.id === user?.id : true;

            if (isPaymentDue && isRelevantSubscription) {
              acc.push({
                entityType,
                entity,
                subscription: entity.subscription,
              });
            }
          }

          return acc;
        }, [])
        ?.sort((entityA, entityB) => {
          const productIdA = entityA.subscription.product?.id;
          const productIdB = entityB.subscription.product?.id;

          const indexA = PRODUCT_ID_PRECEDENCE_ORDER.indexOf(productIdA);
          const indexB = PRODUCT_ID_PRECEDENCE_ORDER.indexOf(productIdB);

          return indexA - indexB;
        })?.[0],
    [profileAccesses, user?.id],
  );

  if (!primaryPaymentDueEntity) {
    return null;
  }

  return (
    <div className={classes.banner}>
      <div className={classes.container}>
        <Typography className={classes.label} variant="p" size={14} weight="bold">
          {t(`${TP}.FN_PAYMENT_DUE_FOR`)}
        </Typography>
        <EntityCard
          className={classes.entityCard}
          entityType={ENTITY_TYPES.ORGANIZATION}
          entity={primaryPaymentDueEntity.entity}
          typographyProps={{
            subtext: {
              truncate: true,
            },
          }}
          isLinkShallow={false}
          trackingData={{
            section: SECTIONS.PAYMENT_DUE_BANNER,
            entityId: primaryPaymentDueEntity.entity?.id,
            entityName: primaryPaymentDueEntity.entity?.name,
            entityType: primaryPaymentDueEntity.entityType,
          }}
        />
        <PrimaryButton
          className={classes.cta}
          size="small"
          shape="rectangle"
          onClick={() =>
            setShowSubscriptionPlans({
              show: true,
              entity: primaryPaymentDueEntity.entity,
              entityType: primaryPaymentDueEntity.entityType,
              subscription: primaryPaymentDueEntity.subscription,
            })
          }
        >
          {t(`${TP}.FN_PAY_NOW`)}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default PaymentDueBanner;
